/**
 * Übernahme-Regeln des SiGe-Plan-Moduls.
 *
 * Hier steht alles, was aus fremden Quellen (Projekt-Stammdaten, KI-Vorschläge,
 * Muster-Vorlage) fertige Zeilen für `sige_projekte` bzw. `sige_arbeitsschritte`
 * macht. Bewusst ohne React und ohne Supabase: der Anlage-Assistent und der
 * Dokumenten-Upload sollen dieselben Regeln benutzen (Hauptabschnitt,
 * Zeitraum-Klemmung, Duplikatschutz) und die Regeln bleiben ohne DOM testbar.
 */

import { HAUPTABSCHNITTE, PlanDarstellung, SigeProject, mitPlanZeitachse, planHatZeitachse } from './types';
import { ABSCHNITT_PHASEN, MUSTER_ZEILEN, MusterZeile } from './musterVorlage';
import { alsIsoDatum, clampDatum, phaseDatum } from './zeitraum';
import { Contact, ReportMetadata } from '../../types';
import type { ProjektMerkmale } from '../planungsphase/types';
import { ergaenzeDokumente, schlageDokumenteVor } from '../../services/mitgeltendeDokumente';
import type { MitgeltendesDokument } from '../../services/mitgeltendeDokumente';
import type { ErgaenzungsAuftrag } from './unterlagenErgaenzen';
import { ohnePersonendaten } from '../../services/aiAssistContext';

// ─────────────────────────────────────────────────────────────────────────────
// Hauptabschnitt aus einem KI-Vorschlag ableiten
// ─────────────────────────────────────────────────────────────────────────────

// Ohne Hauptabschnitt landet ein Schritt in der Liste unter „Ohne Abschnitt" und
// fällt aus der Plan-Reihenfolge. Die KI liefert selten einen brauchbaren Wert,
// deshalb wird er aus Bezeichnung und Gewerk abgeleitet.
const ABSCHNITT_SCHLUESSEL: { abschnitt: string; muster: RegExp }[] = [
  { abschnitt: 'Baustellenvorbereitung', muster: /abbruch|rückbau|kampfmittel|erdverlegt|leitung|baugrund|beweissicher|rodung|freimach|absteck/i },
  { abschnitt: 'Baustelleneinrichtung', muster: /baustelleneinricht|bauzaun|einfried|container|baustrom|bauwasser|kranstellung|einrichtungsfläche/i },
  { abschnitt: 'gemeinsam genutzte Einrichtung: VERKEHRSWEGE', muster: /verkehrsweg|zufahrt|zuwegung|zugang|treppe|leiter|laufsteg|absperr|verkehrssicher/i },
  { abschnitt: 'gemeinsam genutzte Einrichtung: LAGERPLÄTZE', muster: /lagerplatz|lagerfläche|materiallager|stapel|zwischenlager/i },
  { abschnitt: 'gemeinsam genutzte Einrichtung: MASCHINEN, GERÄTE, GERÜSTE', muster: /gerüst|kran|maschine|gerät|hebezeug|bagger|stapler|arbeitsbühne|hubsteiger/i },
  { abschnitt: 'gemeinsam genutzte Einrichtung: UNTERKÜNFTE', muster: /unterkunft|sanitär(?:container|einricht)|pausenraum|tagesunterkunft|waschgelegenheit/i },
  { abschnitt: 'gemeinsam genutzte Einrichtung: ARBEITSSCHUTZ', muster: /ersthelfer|erste hilfe|notruf|rettungs|brandschutz|feuerlösch|psa|unterweisung/i },
  { abschnitt: 'Besondere Arbeitsbedingungen: Lärm, Hitze, Biostoffe', muster: /lärm|hitze|kälte|biostoff|asbest|kmf|gefahrstoff|schadstoff|staubbelast/i },
  { abschnitt: 'gemeinsam genutzte Einrichtung: ARBEITSPLÄTZE', muster: /arbeitsplatz|arbeitsstätte|beleuchtung|witterungsschutz/i },
];

/** Auffangbecken: Gewerke-Arbeiten ohne erkennbare Zuordnung. */
export const FALLBACK_ABSCHNITT = 'ARBEITSVERFAHREN mit Gefährdung anderer Gewerke';

export const abschnittFuerKiSchritt = (step: any, bekannte: string[] = [...HAUPTABSCHNITTE]): string => {
  const vorgabe = typeof step?.hauptabschnitt === 'string' ? step.hauptabschnitt.trim() : '';
  if (vorgabe && bekannte.includes(vorgabe)) return vorgabe;
  const text = `${step?.bezeichnung || ''} ${step?.gewerk || ''}`;
  const treffer = ABSCHNITT_SCHLUESSEL.find(k => k.muster.test(text));
  return treffer?.abschnitt || FALLBACK_ABSCHNITT;
};

// ─────────────────────────────────────────────────────────────────────────────
// KI-Anfragen vorbereiten
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Baubeschreibung für den Arbeitsschritt-Vorschlag der KI: getrimmt und ohne
 * Personendaten. Der Text kommt aus der Planungsphase oder von Hand und kann
 * Namen, Telefonnummern und E-Mail-Adressen enthalten („Ansprechpartner
 * Bauleiter Meier, 0171 …") — die gehören nicht in den Prompt. `ohnePersonen-
 * daten` ersetzt sichtbar statt zu löschen; Regelwerksnummern und Mengen
 * bleiben stehen. Leer, wenn nichts Verwertbares übrig ist.
 */
export const beschreibungFuerKi = (text: string | null | undefined): string =>
  ohnePersonendaten(String(text ?? '').trim()).trim();

// ─────────────────────────────────────────────────────────────────────────────
// KI-Antworten auswerten
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Erstes balanciertes JSON-Objekt/-Array aus einem Text ziehen. Gemini hängt
 * teils Erläuterungstext hinter das JSON — ein reines `JSON.parse` scheitert
 * dann mit „after position N".
 */
const ersterJsonBlock = (s: string): any => {
  const startO = s.indexOf('{');
  const startA = s.indexOf('[');
  const start = startO === -1 ? startA : (startA === -1 ? startO : Math.min(startO, startA));
  if (start === -1) throw new Error('Kein JSON in der KI-Antwort gefunden');
  const open = s[start];
  const close = open === '{' ? '}' : ']';
  let depth = 0, inStr = false, esc = false;
  for (let i = start; i < s.length; i++) {
    const ch = s[i];
    if (inStr) {
      if (esc) esc = false;
      else if (ch === '\\') esc = true;
      else if (ch === '"') inStr = false;
      continue;
    }
    if (ch === '"') inStr = true;
    else if (ch === open) depth++;
    else if (ch === close) {
      depth--;
      if (depth === 0) return JSON.parse(s.slice(start, i + 1));
    }
  }
  throw new Error('Unvollständiges JSON in der KI-Antwort');
};

/**
 * Antwort des KI-Backends in ein Objekt überführen. Die Hülle wechselt je nach
 * Aktion (`{text}`, `{content}`, roher String mit ```json-Zaun, fertiges
 * Objekt) — die Oberfläche soll sich damit nicht befassen müssen.
 */
export const kiAntwortAlsObjekt = (antwort: any): any => {
  let wert = antwort;
  if (wert && typeof wert === 'object' && typeof wert.text === 'string') wert = wert.text;
  if (wert && typeof wert === 'object' && typeof wert.content === 'string') wert = wert.content;
  if (typeof wert !== 'string') {
    if (wert && typeof wert === 'object') return wert;
    throw new Error('Die Antwort der KI war nicht auswertbar.');
  }
  const bereinigt = wert.replace(/```json/gi, '').replace(/```/g, '').trim();
  try {
    return ersterJsonBlock(bereinigt);
  } catch {
    throw new Error('Die Antwort der KI war nicht auswertbar.');
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Zeilen für sige_arbeitsschritte
// ─────────────────────────────────────────────────────────────────────────────

/** Bauzeit des Plans — Untergrenze/Obergrenze aller Schritt-Zeiträume. */
export interface PlanZeitraum {
  von?: string;
  bis?: string;
}

/** Vergleichsform einer Bezeichnung: Groß/Klein und Mehrfach-Leerzeichen egal. */
export const normBezeichnung = (wert?: string): string =>
  (wert || '').trim().toLowerCase().replace(/\s+/g, ' ');

/**
 * Zeitfenster eines Schritts: eine mitgelieferte Angabe gewinnt, sonst wird die
 * typische Phase des Hauptabschnitts auf die Bauzeit umgerechnet. Das Ergebnis
 * liegt immer innerhalb der Bauzeit.
 */
export const zeitfensterFuerAbschnitt = (
  abschnitt: string | undefined,
  rohVon: string,
  rohBis: string,
  zeitraum?: PlanZeitraum,
): { von: string; bis: string } => {
  const phase = ABSCHNITT_PHASEN[abschnitt || ''];
  const vonRoh = rohVon || (phase ? phaseDatum(phase[0], zeitraum?.von, zeitraum?.bis) : '');
  const bisRoh = rohBis || (phase ? phaseDatum(phase[1], zeitraum?.von, zeitraum?.bis) : '');
  return {
    von: clampDatum(vonRoh, zeitraum?.von, zeitraum?.bis),
    bis: clampDatum(bisRoh, zeitraum?.von, zeitraum?.bis),
  };
};

/** Ein von der KI vorgeschlagener Arbeitsschritt (Dokument-Analyse oder Beschreibung). */
export interface KiSchrittVorschlag {
  bezeichnung?: string;
  gewerk?: string;
  hauptabschnitt?: string;
  zeitraumVon?: string | null;
  zeitraumBis?: string | null;
  vorschriften?: string[];
  /** Nur falls die KI ausdrücklich Unterlagen nennt — sonst ergänzt der Katalog. */
  mitgeltende_dokumente?: string[];
  kritisch?: boolean;
  kategorie?: string;
  konfidenz?: number;
}

/** Textliste säubern: nur Zeichenketten, getrimmt, ohne Leereinträge. */
const textListe = (werte?: unknown[] | null): string[] =>
  (Array.isArray(werte) ? werte : []).map(t => String(t ?? '').trim()).filter(Boolean);

/** Was die Vorbelegung der mitgeltenden Dokumente von einer neuen Zeile liest. */
export interface DokumentQuelle {
  bezeichnung: string;
  /** Eigenes Gewerk der Quelle — NICHT das Standard-Gewerk der Übernahme (siehe unten). */
  gewerk?: string | null;
  schutzmassnahmen?: string[] | null;
  vorschriften?: string[] | null;
  /** Ausdrücklich gesetzte Dokumente (Musterzeile, Katalog, KI). */
  mitgeltende_dokumente?: string[] | null;
}

/**
 * Mitgeltende Dokumente einer NEU angelegten Zeile: zuerst die ausdrücklich
 * gesetzten, dahinter die BEDINGUNGSLOSEN Vorschläge des Katalogs aus
 * Bezeichnung und Gewerk. Was schon unter den Dokumenten oder den Vorschriften
 * steht, wird nicht noch einmal vorgeschlagen; Dubletten (auch
 * Alias-Schreibweisen) fallen weg.
 *
 * Vorschläge mit Bedingung („nur im Bestand", „bei > 5 m …") bleiben bei der
 * automatischen Übernahme (Muster, KI, Upload) draußen: ob die Bedingung
 * zutrifft, weiß nur der Bearbeiter — sonst druckte ein Neubau-Plan etwa das
 * Brandschutzkonzept eines Bestandsgebäudes. Der Editor bietet sie weiter an;
 * kennt die Planungsphase das passende Merkmal als wahr, löst
 * `bedingteDokumenteNachMerkmalen` die Bedingung auf — auch dann nur als
 * Vorschlag, übernommen wird per Klick im Assistenten.
 * Ausdrücklich gesetzte Dokumente der Quelle bleiben immer stehen.
 *
 * Das Standard-Gewerk aus dem Verteiler fließt bewusst NICHT ein: es wird auf
 * alle Musterzeilen ohne eigenes Gewerk gestempelt und löste sonst bei jeder
 * Zeile dieselben gewerkbezogenen Dokumente aus.
 *
 * Ergebnis ist immer ein Array: die Spalte ist `not null`, und bei einem
 * Sammel-Insert setzt supabase-js einen Schlüssel, der nur in einzelnen
 * Zeilen fehlt, für diese Zeilen auf NULL.
 */
export const mitgeltendeFuerNeueZeile = (q: DokumentQuelle): string[] => {
  const explizit = ergaenzeDokumente([], textListe(q.mitgeltende_dokumente));
  const vorschlaege = schlageDokumenteVor({
    bezeichnung: q.bezeichnung,
    gewerk: q.gewerk || '',
    schutzmassnahmen: textListe(q.schutzmassnahmen),
    vorhanden: [...explizit, ...textListe(q.vorschriften)],
  })
    .filter(v => v.bedingungen.length === 0)
    .map(v => v.dokument.name);
  return ergaenzeDokumente(explizit, vorschlaege);
};

/** Ein Arbeitsschritt des Plans, so weit die Dokumentvorschläge ihn lesen. */
export interface SchrittFuerDokumente extends DokumentQuelle {
  id: string;
  hauptabschnitt?: string | null;
}

/** Ein bedingter Katalogvorschlag, dessen Bedingung die Planungsphase bejaht. */
export interface BedingterDokumentVorschlag {
  schrittId: string;
  bezeichnung: string;
  hauptabschnitt: string;
  dokument: MitgeltendesDokument;
  /** Wortlaut der Bedingung(en) aus dem Katalog — der Bearbeiter sieht, was er bejaht. */
  bedingungen: string[];
  /** Merkmale der Planungsphase, die ausdrücklich wahr sind und die Bedingung tragen. */
  merkmale: (keyof ProjektMerkmale)[];
}

/**
 * Bedingte Katalogvorschläge, deren Bedingung nach den Merkmalen der
 * Planungsphase zutrifft: Vorschlag mit Bedingung UND das Dokument trägt im
 * Katalog ein Merkmal, das ausdrücklich `true` ist. Ein ungeklärtes Merkmal
 * löst nichts auf — sonst stünde das Brandschutzkonzept an jeder Zeile, bis
 * jemand „kein Bestand" angibt. Dokumente ohne Merkmal im Katalog bleiben
 * Sache des Editors.
 *
 * Was am Schritt schon steht (Dokumente, Vorschriften, wörtlich in den
 * Maßnahmen), schlägt der Katalog nicht erneut vor — nach der Übernahme
 * verschwindet der Vorschlag von selbst. Ohne Planungsphase (`null`) leer.
 *
 * Reihenfolge: Schritte wie übergeben, Dokumente in Katalogreihenfolge. Hier
 * wird NICHTS geschrieben; die Übernahme läuft über die Schreib-Warteschlange
 * (`unterlagenErgaenzen.schreibeErgaenzungen`).
 */
export const bedingteDokumenteNachMerkmalen = (
  schritte: readonly SchrittFuerDokumente[],
  merkmale: ProjektMerkmale | null,
): BedingterDokumentVorschlag[] => {
  if (!merkmale) return [];
  const werte = merkmale as Record<string, unknown>;
  const aus: BedingterDokumentVorschlag[] = [];
  (schritte || []).forEach(s => {
    const bezeichnung = String(s?.bezeichnung || '').trim();
    if (!bezeichnung || !s.id) return;
    schlageDokumenteVor({
      bezeichnung,
      gewerk: s.gewerk || '',
      schutzmassnahmen: textListe(s.schutzmassnahmen),
      vorhanden: [...textListe(s.mitgeltende_dokumente), ...textListe(s.vorschriften)],
    }).forEach(v => {
      if (v.bedingungen.length === 0) return;
      const wahr = (v.dokument.merkmale || []).filter(k => werte[k] === true);
      if (!wahr.length) return;
      aus.push({
        schrittId: s.id,
        bezeichnung,
        hauptabschnitt: s.hauptabschnitt || '',
        dokument: v.dokument,
        bedingungen: [...v.bedingungen],
        merkmale: wahr,
      });
    });
  });
  return aus;
};

/**
 * Angeklickte bedingte Vorschläge → Schreibaufträge für
 * `unterlagenErgaenzen.schreibeErgaenzungen` (ein Auftrag je Schritt, Namen
 * in Vorschlagsreihenfolge, ohne Doppelte). Gerechnet gegen den AKTUELLEN
 * Stand der Schritte; Schritte, die es nicht mehr gibt oder die das Dokument
 * inzwischen tragen, fallen heraus. Geschrieben wird erst beim Ausführen
 * des Auftrags — gegen die dann frische Zeile.
 */
export const bedingteAlsAuftraege = (
  gewaehlt: readonly BedingterDokumentVorschlag[],
  aktuell: readonly SchrittFuerDokumente[],
): ErgaenzungsAuftrag[] => {
  const nachId = new Map(aktuell.map(s => [s.id, s]));
  const jeSchritt = new Map<string, string[]>();
  gewaehlt.forEach(v => {
    const liste = jeSchritt.get(v.schrittId) ?? [];
    if (!liste.includes(v.dokument.name)) liste.push(v.dokument.name);
    jeSchritt.set(v.schrittId, liste);
  });
  const aus: ErgaenzungsAuftrag[] = [];
  jeSchritt.forEach((namen, schrittId) => {
    const schritt = nachId.get(schrittId);
    if (!schritt) return;
    const vorher = textListe(schritt.mitgeltende_dokumente);
    const dokumente = ergaenzeDokumente(vorher, namen);
    const hinzu = dokumente.filter(n => !vorher.includes(n));
    if (hinzu.length) aus.push({ schrittId, bezeichnung: schritt.bezeichnung, dokumente, hinzu });
  });
  return aus;
};

export interface ZeilenOptionen {
  sigeProjektId: string;
  zeitraum?: PlanZeitraum;
  /** Sortierung setzt hinter den bestehenden Schritten auf. */
  startSortierung?: number;
  bekannteAbschnitte?: string[];
  /** Schon im Plan stehende Bezeichnungen — verhindert Doppelanlagen. */
  vorhandeneBezeichnungen?: string[];
  /** Fällt ein, wenn die Quelle kein Gewerk nennt (Vorschlag aus dem Verteiler). */
  standardGewerk?: string;
  /**
   * Bauzeitenplan liegt noch nicht vor (Plan ohne Zeitachse): neue Zeilen
   * bekommen KEINEN Zeitraum — weder den der KI noch einen aus der Phase
   * geschätzten. Datiert wird mit dem Bauzeitenplan.
   */
  ohneZeitraeume?: boolean;
}

/** Zeitraum einer neuen Zeile: leer ohne Zeitachse, sonst wie `zeitfensterFuerAbschnitt`. */
const zeitfensterNeueZeile = (
  opt: ZeilenOptionen,
  abschnitt: string | undefined,
  rohVon: string,
  rohBis: string,
): { von: string; bis: string } =>
  opt.ohneZeitraeume ? { von: '', bis: '' } : zeitfensterFuerAbschnitt(abschnitt, rohVon, rohBis, opt.zeitraum);

export interface ZeilenErgebnis {
  rows: Record<string, any>[];
  /** Übersprungen, weil die Bezeichnung bereits im Plan steht. */
  duplikate: string[];
  /** Übersprungen, weil keine Bezeichnung geliefert wurde. */
  ohneBezeichnung: number;
}

/** Sammelt die schon belegten Bezeichnungen und meldet Duplikate im selben Lauf. */
const belegtSet = (vorhandene?: string[]): Set<string> =>
  new Set((vorhandene || []).map(normBezeichnung).filter(Boolean));

/** KI-Vorschläge → Zeilen für `sige_arbeitsschritte`. */
export const baueKiZeilen = (vorschlaege: KiSchrittVorschlag[], opt: ZeilenOptionen): ZeilenErgebnis => {
  const bekannte = opt.bekannteAbschnitte?.length ? opt.bekannteAbschnitte : ([...HAUPTABSCHNITTE] as string[]);
  const belegt = belegtSet(opt.vorhandeneBezeichnungen);
  const rows: Record<string, any>[] = [];
  const duplikate: string[] = [];
  let ohneBezeichnung = 0;
  let sortierung = opt.startSortierung ?? 0;

  (vorschlaege || []).forEach(v => {
    const bezeichnung = String(v?.bezeichnung || '').trim();
    if (!bezeichnung) {
      ohneBezeichnung++;
      return;
    }
    const schluessel = normBezeichnung(bezeichnung);
    if (belegt.has(schluessel)) {
      duplikate.push(bezeichnung);
      return;
    }
    belegt.add(schluessel);

    const abschnitt = abschnittFuerKiSchritt(v, bekannte);
    const { von, bis } = zeitfensterNeueZeile(opt, abschnitt, alsIsoDatum(v.zeitraumVon), alsIsoDatum(v.zeitraumBis));

    const row: Record<string, any> = {
      sige_projekt_id: opt.sigeProjektId,
      sortierung: sortierung++,
      bezeichnung,
      gewerk: (v.gewerk || opt.standardGewerk || '').trim(),
      hauptabschnitt: abschnitt,
      zeitraum_von: von || null,
      zeitraum_bis: bis || null,
    };
    const vorschriften = (v.vorschriften || []).map(t => String(t || '').trim()).filter(Boolean);
    if (vorschriften.length) row.vorschriften = vorschriften;
    // Immer gesetzt (auch leer) — siehe mitgeltendeFuerNeueZeile.
    row.mitgeltende_dokumente = mitgeltendeFuerNeueZeile({
      bezeichnung,
      gewerk: v.gewerk,
      vorschriften,
      mitgeltende_dokumente: v.mitgeltende_dokumente,
    });
    if (v.kritisch) row.kritisch = true;
    rows.push(row);
  });

  return { rows, duplikate, ohneBezeichnung };
};

/** Muster-Zeilen → Zeilen für `sige_arbeitsschritte` (alle Fachspalten der Vorlage). */
export const baueMusterZeilen = (zeilen: MusterZeile[], opt: ZeilenOptionen): ZeilenErgebnis => {
  const belegt = belegtSet(opt.vorhandeneBezeichnungen);
  const rows: Record<string, any>[] = [];
  const duplikate: string[] = [];
  let ohneBezeichnung = 0;
  let sortierung = opt.startSortierung ?? 0;

  (zeilen || []).forEach(m => {
    const bezeichnung = String(m?.bezeichnung || '').trim();
    if (!bezeichnung) {
      ohneBezeichnung++;
      return;
    }
    const schluessel = normBezeichnung(bezeichnung);
    if (belegt.has(schluessel)) {
      duplikate.push(bezeichnung);
      return;
    }
    belegt.add(schluessel);

    const { von, bis } = zeitfensterNeueZeile(opt, m.hauptabschnitt, '', '');
    const codes = m.hinweis_codes || [];
    rows.push({
      sige_projekt_id: opt.sigeProjektId,
      sortierung: sortierung++,
      bezeichnung,
      gewerk: (m.gewerk || opt.standardGewerk || '').trim(),
      hauptabschnitt: m.hauptabschnitt || null,
      zeitraum_von: von || null,
      zeitraum_bis: bis || null,
      schutzmassnahmen: m.schutzmassnahmen || [],
      vorschriften: m.vorschriften || [],
      mitgeltende_dokumente: mitgeltendeFuerNeueZeile({
        bezeichnung,
        gewerk: m.gewerk,
        schutzmassnahmen: m.schutzmassnahmen,
        vorschriften: m.vorschriften,
        mitgeltende_dokumente: m.mitgeltende_dokumente,
      }),
      hinweis_codes: codes,
      hinweis_code: codes.join('; ') || m.hinweis_code || null,
      kritisch: !!m.kritisch,
      koordinierungsbedarf: m.koordinierungsbedarf || null,
      zustaendig_erstellung: m.zustaendig_erstellung || null,
      zustaendig_kontrolle: m.zustaendig_kontrolle || null,
      zustaendig_instandhaltung: m.zustaendig_instandhaltung || null,
    });
  });

  return { rows, duplikate, ohneBezeichnung };
};

/**
 * Muster-Vorlage nach Hauptabschnitt gruppieren — Reihenfolge wie im Plan,
 * unbekannte Abschnitte hängen hinten an. Die Indizes zeigen auf die
 * übergebene Liste, damit die Oberfläche einzelne Zeilen abwählen kann.
 */
export const gruppiereMusterZeilen = (
  zeilen: MusterZeile[] = MUSTER_ZEILEN,
): { abschnitt: string; indizes: number[] }[] => {
  const reihenfolge = [...HAUPTABSCHNITTE] as string[];
  const gruppen = new Map<string, number[]>();
  (zeilen || []).forEach((z, i) => {
    const key = z.hauptabschnitt || 'Ohne Abschnitt';
    if (!gruppen.has(key)) gruppen.set(key, []);
    gruppen.get(key)!.push(i);
  });
  return Array.from(gruppen.entries())
    .map(([abschnitt, indizes]) => ({ abschnitt, indizes }))
    .sort((a, b) => {
      const ia = reihenfolge.indexOf(a.abschnitt);
      const ib = reihenfolge.indexOf(b.abschnitt);
      return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
    });
};

/** Gewerke-Vorschläge aus dem Projektverteiler (Gewerk, sonst Rolle). */
export const gewerkVorschlaege = (beteiligte: Pick<Contact, 'tradeType' | 'role' | 'company'>[]): string[] => {
  const set = new Set<string>();
  (beteiligte || []).forEach(b => {
    const gewerk = (b?.tradeType || b?.role || '').trim();
    if (gewerk) set.add(gewerk);
  });
  return Array.from(set).sort((a, b) => a.localeCompare(b, 'de'));
};

// ─────────────────────────────────────────────────────────────────────────────
// Plankopf
// ─────────────────────────────────────────────────────────────────────────────

export type PlankopfForm = Partial<SigeProject>;

/**
 * Projekt-Stammdaten → Plankopf. Gleiche Regel wie im Stempelfeld-Editor:
 * bereits gepflegte Werte bleiben stehen, leere Felder werden gefüllt.
 * `heute` ist ein Parameter, damit das Standard-Plandatum testbar bleibt.
 */
export const plankopfAusStammdaten = (
  meta: ReportMetadata | undefined,
  projektName?: string,
  bestehend?: PlankopfForm | null,
  heute: string = new Date().toISOString().split('T')[0],
): PlankopfForm => {
  const alt: PlankopfForm = bestehend || {};
  const wert = (neu?: string, vorhanden?: string) => (vorhanden || '').trim() || (neu || '').trim();
  return {
    bauherr_name: wert(meta?.clientName, alt.bauherr_name),
    bauherr_adresse: wert(meta?.clientAddress, alt.bauherr_adresse),
    baumassnahme_name: wert(meta?.projectName || projektName, alt.baumassnahme_name),
    baumassnahme_adresse: wert(meta?.projectAddress, alt.baumassnahme_adresse),
    plan_nr: wert(meta?.projectNumber ? `SGP-${meta.projectNumber}` : '', alt.plan_nr),
    plan_datum: (alt.plan_datum || '').trim() || heute,
    plan_inhalt: wert('Sicherheits- und Gesundheitsschutzplan', alt.plan_inhalt),
    sigeko_name: wert(meta?.coordinator, alt.sigeko_name),
    sigeko_mobil: wert(meta?.coordinatorPhone, alt.sigeko_mobil),
    sigeko_email: wert(meta?.coordinatorEmail, alt.sigeko_email),
    zeitraum_von: wert(meta?.globalData?.startDate, alt.zeitraum_von),
    zeitraum_bis: wert(meta?.globalData?.endDate, alt.zeitraum_bis),
    bearbeitet_name: wert(meta?.coordinator, alt.bearbeitet_name),
    // Aus der Darstellung nur der Haken „Bauzeitenplan liegt noch nicht vor"
    // — Farben, Schrift und Verantwortliche pflegt das Stempelfeld.
    ...(planHatZeitachse(alt.darstellung) ? {} : { darstellung: mitPlanZeitachse(null, false) }),
  };
};

/**
 * Weiter-Bedingung in Schritt 1: ohne Bauherr, Baumaßnahme und vollständige
 * Bauzeit taugen weder Terminautomatik noch PDF-Zeitachse etwas. Ohne
 * Zeitachse (Bauzeitenplan liegt noch nicht vor) darf die Bauzeit fehlen.
 */
export const plankopfBereit = (form: PlankopfForm | null | undefined): boolean =>
  !!(form?.bauherr_name || '').trim() &&
  !!(form?.baumassnahme_name || '').trim() &&
  (!planHatZeitachse(form?.darstellung) || (
    !!(form?.zeitraum_von || '').trim() &&
    !!(form?.zeitraum_bis || '').trim()
  ));

/**
 * `darstellung`, die Schritt 1 schreiben muss, oder `undefined`, wenn der
 * Plan den Haken „Bauzeitenplan liegt noch nicht vor" schon so trägt. Aus dem
 * Formular kommt NUR dieser Haken; alles andere bleibt, wie es im Plan steht
 * (`plan` = zuletzt gelesener Stand, bei der Neuanlage null). `plankopfZuDb`
 * schreibt die Darstellung nicht — sie gehört dem Stempelfeld.
 */
export const plankopfDarstellung = (
  form: PlankopfForm,
  plan?: Pick<SigeProject, 'darstellung'> | null,
): PlanDarstellung | undefined => {
  const an = planHatZeitachse(form.darstellung);
  return an === planHatZeitachse(plan?.darstellung) ? undefined : mitPlanZeitachse(plan?.darstellung, an);
};

/**
 * Plankopf-Formular → Schreibform. Leere Datumsfelder müssen zu NULL werden,
 * sonst quittiert die Datenbank den Leerstring mit einem Cast-Fehler.
 */
export const plankopfZuDb = (form: PlankopfForm, projectId?: string): Record<string, any> => {
  const text = (v?: string) => (v || '').trim();
  const datum = (v?: string) => text(v) || null;
  const payload: Record<string, any> = {
    bauherr_name: text(form.bauherr_name),
    bauherr_adresse: text(form.bauherr_adresse),
    baumassnahme_name: text(form.baumassnahme_name),
    baumassnahme_adresse: text(form.baumassnahme_adresse),
    plan_nr: text(form.plan_nr),
    plan_datum: datum(form.plan_datum),
    plan_inhalt: text(form.plan_inhalt) || 'Sicherheits- und Gesundheitsschutzplan',
    sigeko_name: text(form.sigeko_name),
    sigeko_mobil: text(form.sigeko_mobil),
    sigeko_email: text(form.sigeko_email),
    zeitraum_von: datum(form.zeitraum_von),
    zeitraum_bis: datum(form.zeitraum_bis),
    bearbeitet_name: text(form.bearbeitet_name),
  };
  if (projectId) payload.project_id = projectId;
  return payload;
};
